import './ladder.css';

import {
  type Ladder,
  type LadderBoard,
  type LadderEntry,
  type LadderQuery,
  type LadderScope,
} from '@shefira/shared';

import { type DomAtlas, loadCharacterAtlas } from '../assets/dom-atlas.js';
import { fetchLadder } from '../net/ladder.js';
import { isAction } from '../settings/keybindings.js';
import { closeButton, hudShortcutButton } from './hud.js';
import { formatDps } from './item-tooltip.js';
import { kitTitle } from './kit.js';
import { headPortrait } from './portrait.js';
import { uiFormat, uiNumber, uiText } from './text.js';

export interface LadderPanelOptions {
  token: string;
}

const BOARDS: readonly { id: LadderBoard; label: string }[] = [
  { id: 'xp', label: 'Expérience' },
  { id: 'dps', label: 'DPS' },
];

const SCOPES: readonly { id: LadderScope; label: string }[] = [
  { id: 'all', label: 'Serveur' },
  { id: 'friends', label: 'Amis' },
  { id: 'guild', label: 'Guilde' },
];

/** A board read less than this long ago is shown again without asking. */
const FRESH_MS = 20_000;

/**
 * The ladder (ADR 0082): the first fifty by experience or by DPS, on the
 * whole server, among the reader's friends or in their guild, and the
 * reader's own line below when they are further down. Read over HTTP when
 * the window opens or a tab changes; the server ranks, this only shows.
 */
export class LadderPanel {
  private readonly root: HTMLElement;
  private readonly boards: HTMLElement;
  private readonly scopes: HTMLElement;
  private readonly list: HTMLElement;
  private readonly foot: HTMLElement;
  private readonly button: HTMLButtonElement;
  private query: LadderQuery = { board: 'xp', scope: 'all' };
  /** The characters' atlas, for the heads; the board draws without them until it loads. */
  private atlas: DomAtlas | null = null;
  private shown: Ladder | null = null;
  private readonly read = new Map<string, { at: number; ladder: Ladder }>();
  /** Only the last request draws: a slow answer for a tab already left is dropped. */
  private asked = 0;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (event.key === 'Escape' && !this.root.hidden) {
      this.close();
      return;
    }
    if (!isAction(event, 'ladder')) return;
    event.preventDefault();
    this.toggle();
  };

  constructor(private readonly options: LadderPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'ladder';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Classement'));

    const title = document.createElement('h2');
    title.className = 'ladder__title';
    kitTitle(title, 'ladder');
    title.append(
      uiText('Classement'),
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    this.boards = document.createElement('div');
    this.boards.className = 'ladder__boards';
    this.boards.setAttribute('role', 'tablist');
    this.scopes = document.createElement('div');
    this.scopes.className = 'ladder__scopes';
    this.list = document.createElement('ol');
    this.list.className = 'ladder__list';
    this.foot = document.createElement('p');
    this.foot.className = 'ladder__foot';
    this.root.append(title, this.boards, this.scopes, this.list, this.foot);
    document.body.append(this.root);

    this.button = hudShortcutButton('ladder', 'Classement', 'ladder', 'top');
    this.button.addEventListener('click', () => {
      this.toggle();
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.renderTabs();
    void loadCharacterAtlas().then((atlas) => {
      this.atlas = atlas;
      if (this.shown !== null) this.render(this.shown);
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    if (this.root.hidden) this.open();
    else this.close();
  }

  open(): void {
    this.root.hidden = false;
    void this.load();
  }

  close(): void {
    this.root.hidden = true;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
    this.button.remove();
  }

  private choose(next: Partial<LadderQuery>): void {
    this.query = { ...this.query, ...next };
    this.renderTabs();
    void this.load();
  }

  private renderTabs(): void {
    this.boards.replaceChildren(
      ...BOARDS.map(({ id, label }) =>
        tab('ladder__board', label, this.query.board === id, () => {
          this.choose({ board: id });
        }),
      ),
    );
    this.scopes.replaceChildren(
      ...SCOPES.map(({ id, label }) =>
        tab('ladder__scope', label, this.query.scope === id, () => {
          this.choose({ scope: id });
        }),
      ),
    );
  }

  private async load(): Promise<void> {
    const query = this.query;
    const key = `${query.board}|${query.scope}`;
    const known = this.read.get(key);
    if (known !== undefined && Date.now() - known.at < FRESH_MS) {
      this.render(known.ladder);
      return;
    }
    const ask = ++this.asked;
    if (known !== undefined) this.render(known.ladder);
    else this.message('Chargement…');
    try {
      const ladder = await fetchLadder(this.options.token, query);
      this.read.set(key, { at: Date.now(), ladder });
      if (ask === this.asked) this.render(ladder);
    } catch {
      if (ask === this.asked)
        this.message('Le classement ne répond pas. Réessaie dans un instant.');
    }
  }

  private message(text: string): void {
    const line = document.createElement('li');
    line.className = 'ladder__empty';
    line.textContent = uiText(text);
    this.list.replaceChildren(line);
    this.foot.textContent = '';
  }

  private render(ladder: Ladder): void {
    this.shown = ladder;
    if (ladder.entries.length === 0) {
      this.message(
        ladder.scope === 'guild'
          ? 'Rejoins une guilde pour te mesurer à ses membres.'
          : 'Personne n’est encore classé.',
      );
      return;
    }
    const rows = ladder.entries.map((entry) => row(entry, ladder.board, this.atlas));
    if (ladder.mine !== null) {
      const gap = document.createElement('li');
      gap.className = 'ladder__gap';
      gap.setAttribute('aria-hidden', 'true');
      gap.textContent = '…';
      rows.push(gap, row(ladder.mine, ladder.board, this.atlas));
    }
    this.list.replaceChildren(...rows);
    // The top first; the reader's line is sought only when it is among the fifty.
    this.list.scrollTop = 0;
    if (ladder.mine === null) {
      this.list.querySelector('.ladder__row--self')?.scrollIntoView({ block: 'nearest' });
    }
    this.foot.textContent = uiFormat('{count} aventuriers classés', {
      count: uiNumber(ladder.total),
    });
  }
}

function tab(
  className: string,
  label: string,
  active: boolean,
  onClick: () => void,
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  button.classList.toggle(`${className}--active`, active);
  button.setAttribute('aria-pressed', String(active));
  button.textContent = uiText(label);
  button.addEventListener('click', onClick);
  return button;
}

function row(entry: LadderEntry, board: LadderBoard, atlas: DomAtlas | null): HTMLLIElement {
  const line = document.createElement('li');
  line.className = 'ladder__row';
  line.classList.toggle('ladder__row--self', entry.self);
  // The first three wear gold, silver and bronze.
  if (entry.rank <= 3) line.dataset.podium = String(entry.rank);

  const rank = document.createElement('span');
  rank.className = 'ladder__rank';
  rank.textContent = uiNumber(entry.rank);

  // The head as they are dressed, in a slot like the friends' list.
  const slot = document.createElement('span');
  slot.className = 'ladder__portrait';
  if (atlas !== null) slot.append(headPortrait(atlas, entry.portrait));

  const who = document.createElement('span');
  who.className = 'ladder__who';
  const name = document.createElement('span');
  name.className = 'ladder__name';
  name.textContent = uiText(entry.name);
  who.append(name);
  if (entry.guild !== null) {
    const guild = document.createElement('span');
    guild.className = 'ladder__guild';
    guild.textContent = `[${entry.guild}]`;
    who.append(guild);
  }

  const level = document.createElement('span');
  level.className = 'ladder__level';
  level.textContent = uiFormat('Niv. {level}', { level: entry.level });

  const value = document.createElement('span');
  value.className = 'ladder__value';
  value.textContent =
    board === 'xp'
      ? uiFormat('{xp} XP', { xp: uiNumber(entry.xp) })
      : entry.dps === null
        ? '—'
        : formatDps(entry.dps);

  line.append(rank, slot, who, level, value);
  return line;
}
